import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import boxOfferDropInIcon from "@images/illustrations/boxOfferDropInIcon.svg"
import boxOfferMillenniumSvg from "@images/illustrations/boxOfferMillenniumIcon.svg"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import boxOfferTicketIcon from "@images/illustrations/boxOfferTicketIcon.svg"
import boxOfferTrialIcon from "@images/illustrations/boxOfferTrialIcon.svg"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { UnionIcon } from "@images/svgIcons/unionIcon"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxOfferLiteVersion, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { isValueInEnum } from "@modules/utils/utils"
import { Check } from "lucide-react"
import { type ReactNode } from "react"

type BoxOfferSlideCardsViewProps = {
  selectedType?: BoxOfferType
}

export function BoxOfferSlideCardsView({ selectedType }: BoxOfferSlideCardsViewProps) {
  const { navigateToBoxOfferForm } = useBoxOfferSidebar()
  const canCreateOffer = useBoxStaffPermission(BoxStaffPermissions.CREATE_OFFER)
  const cards = boxOffersInformation(selectedType).map((offerCard) => (
    <BoxOfferCard key={offerCard.type} {...offerCard} onClick={() => void navigateToBoxOfferForm(offerCard.type)} />
  ))

  // PERMISSIONS OPTIONAL - Head-coach & Coach & Financial permissions denied
  if (!canCreateOffer) {
    return null
  }

  return (
    <div className="flex flex-col gap-4 px-5 pt-12 pb-8">
      <Text fontStyle="TL">{word("box.offer.add.title")}</Text>
      <div className="mt-8 grid auto-rows-fr grid-cols-2 gap-4">{cards}</div>
    </div>
  )
}

type BoxOfferCardConstructor = {
  advantages: string[]
  onClick: () => void
  icon: ReactNode
  type: BoxOfferType
}

const BoxOfferCard = ({ icon, advantages, onClick, type }: BoxOfferCardConstructor) => {
  const { result: box } = useCurrentBox()
  if (!box) {
    return null
  }
  const isBoxOfferLiteVersion =
    box.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
    box.stripeAccountStatus === StripeAccountStatus.ONBOARDING ||
    box.stripeAccountStatus === StripeAccountStatus.UNDEFINED
      ? true
      : false

  const isDisabled = isBoxOfferLiteVersion && !isValueInEnum(type, BoxOfferLiteVersion)

  return (
    <div
      className={cn(
        "relative isolate flex h-full flex-col gap-4 overflow-hidden rounded-2xl bg-interactive-background-neutral-disabled p-6 transition-colors",
        isDisabled && "opacity-50",
      )}
    >
      <div className="flex items-center justify-center gap-2">
        {icon}
        <Text fontStyle="TM" className="font-bold text-textDefault-body">
          {word(`box.offer.form.types.${type}`)}
        </Text>
      </div>

      <div className="flex flex-col gap-3">
        <Text fontStyle="LS" className="self-start text-textOnSurface-subtle">
          {word("offer.form.advantages.subtitle")}
        </Text>
        {advantages.map((advantage, index) => (
          <div key={`${index}-${advantage}`} className="flex w-full items-start gap-2">
            <Check size={20} color="currentColor" className="mt-0.5 shrink-0 text-textSuccess" />
            <Text fontStyle="LM" className="text-textDefault-body">
              {advantage}
            </Text>
          </div>
        ))}
      </div>

      <Button
        variant="primary"
        onClick={(e) => {
          e.stopPropagation()
          if (!isDisabled) onClick()
        }}
        className={"mt-auto w-full"}
        disabled={isDisabled}
      >
        <PlusIcon size={14} color="currentColor" />
        {word(`offer.form.${type}.create`)}
      </Button>
      <UnionIcon
        color="currentColor"
        className="absolute -right-2.5 -bottom-2.5 -z-1 size-45 text-textDefault-body opacity-25"
      />
    </div>
  )
}

export const boxOffersInformation = (selectedType?: BoxOfferType) => {
  const offers = [
    {
      type: BoxOfferType.Subscription,
      advantages: [
        word("offer.form.advantages.subscription.advantage1"),
        word("offer.form.advantages.subscription.advantage2"),
        word("offer.form.advantages.subscription.advantage3"),
      ],
      icon: <img src={boxOfferPlanIcon} alt="boxOfferPlanIcon" />,
    },
    {
      type: BoxOfferType.TicketsBook,
      advantages: [
        word("offer.form.advantages.ticketsBook.advantage1"),
        word("offer.form.advantages.ticketsBook.advantage2"),
        word("offer.form.advantages.ticketsBook.advantage3"),
      ],
      icon: <img src={boxOfferTicketIcon} alt="boxOfferTicketIcon" />,
    },
    {
      type: BoxOfferType.Trial,
      advantages: [
        word("offer.form.advantages.trial.advantage1"),
        word("offer.form.advantages.trial.advantage2"),
        word("offer.form.advantages.trial.advantage3"),
      ],
      icon: <img src={boxOfferTrialIcon} alt="boxOfferTrialIcon" />,
    },
    {
      type: BoxOfferType.DropIn,
      advantages: [
        word("offer.form.advantages.dropIn.advantage1"),
        word("offer.form.advantages.dropIn.advantage2"),
        word("offer.form.advantages.dropIn.advantage3"),
      ],
      icon: <img src={boxOfferDropInIcon} alt="boxOfferDropInIcon" />,
    },
    {
      type: BoxOfferType.Millenium,
      advantages: [
        word("offer.form.advantages.millenium.advantage1"),
        word("offer.form.advantages.millenium.advantage2"),
        word("offer.form.advantages.millenium.advantage3"),
      ],
      icon: <img src={boxOfferMillenniumSvg} alt="boxOfferMillenniumSvg" />,
    },
  ]

  if (!selectedType) {
    return offers
  }
  const selectedOffers = offers.filter((offer) => offer.type === selectedType)
  const otherOffers = offers.filter((offer) => offer.type !== selectedType)

  return selectedOffers.concat(otherOffers)
}
